import { useEffect, useRef, useState } from 'react';

/**
 * Pavé de signature manuscrite (CDC §5.4).
 *
 * Tracé au doigt ou au stylet, comme sur la tablette de la maquette. Les
 * événements pointeur couvrent souris, tactile et stylet d'un seul jeu, sans
 * dépendance.
 *
 * **Le pavé ne se vide pas de lui-même.** Son canvas n'est reconstruit qu'à
 * l'hydratation, c'est-à-dire quand `traceInitiale` change : un parent qui le
 * garde monté en changeant seulement de signataire lui laisse le tracé du
 * précédent, et la personne suivante signe par-dessus celle d'avant. Remettre
 * l'état du parent à `null` n'y change rien — c'est le bitmap qui persiste.
 * Un parent qui enchaîne des signataires doit donc lui donner une `key` qui
 * change avec le signataire, comme le fait le mode signature de l'émargement.
 */
export function PaveSignature({
  onChanger,
  desactive,
  traceInitiale,
  hauteur = 'h-40',
}: {
  /** Reçoit le tracé en data URL PNG, ou `null` quand le pavé est vide. */
  onChanger: (trace: string | null) => void;
  desactive?: boolean;
  traceInitiale?: string | null;
  /**
   * Hauteur du pavé, en classe Tailwind. Le défaut convient à une modale ; le
   * mode signature de l'émargement, où l'on signe au doigt en tenant la
   * tablette, en demande bien davantage.
   */
  hauteur?: string;
}): JSX.Element {
  const toile = useRef<HTMLCanvasElement>(null);
  const dessine = useRef(false);
  const aTrace = useRef(false);
  // Le parent peut remonter le PNG émis à la fin du tracé. Ce marqueur permet
  // de distinguer cet écho d'une véritable nouvelle valeur à hydrater.
  const derniereTraceEmise = useRef<string | null | undefined>(undefined);
  const [vide, setVide] = useState(!traceInitiale);

  function initialiserToile(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
    const rect = canvas.getBoundingClientRect();
    const densite = window.devicePixelRatio || 1;

    // Modifier width/height remet le bitmap du canvas à zéro. Cette opération
    // est donc confinée à l'initialisation et à une hydratation explicite,
    // jamais à un rendu déclenché par la saisie du formulaire.
    canvas.width = Math.round(rect.width * densite);
    canvas.height = Math.round(rect.height * densite);

    const contexte = canvas.getContext('2d');
    if (!contexte) return null;
    contexte.setTransform(densite, 0, 0, densite, 0, 0);
    contexte.lineWidth = 2;
    contexte.lineCap = 'round';
    contexte.lineJoin = 'round';
    contexte.strokeStyle = '#0B2A5E';
    return contexte;
  }

  useEffect(() => {
    const canvas = toile.current;
    if (!canvas) return;

    // Ne pas redimensionner lorsque le parent nous renvoie le PNG que nous
    // venons de lui transmettre : ce cycle était la cause de l'effacement.
    if (
      derniereTraceEmise.current !== undefined &&
      traceInitiale === derniereTraceEmise.current
    ) {
      return;
    }

    const contexte = initialiserToile(canvas);
    if (!contexte) return;
    aTrace.current = false;
    setVide(!traceInitiale);

    if (traceInitiale) {
      const rect = canvas.getBoundingClientRect();
      const img = new Image();
      img.onload = () => {
        contexte.drawImage(img, 0, 0, rect.width, rect.height);
        setVide(false);
        aTrace.current = true;
      };
      img.src = traceInitiale;
    }
  }, [traceInitiale]);

  function position(evenement: React.PointerEvent<HTMLCanvasElement>): [number, number] {
    const rect = evenement.currentTarget.getBoundingClientRect();
    return [evenement.clientX - rect.left, evenement.clientY - rect.top];
  }

  function commencer(evenement: React.PointerEvent<HTMLCanvasElement>): void {
    if (desactive) return;
    const contexte = toile.current?.getContext('2d');
    if (!contexte) return;

    evenement.currentTarget.setPointerCapture(evenement.pointerId);
    dessine.current = true;
    const [x, y] = position(evenement);
    contexte.beginPath();
    contexte.moveTo(x, y);
    // Un appui bref est une signature valide : on dépose un point tout de
    // suite, au lieu d'attendre un premier événement de déplacement.
    contexte.lineTo(x + 0.01, y + 0.01);
    contexte.stroke();
    aTrace.current = true;
    setVide(false);
  }

  function tracer(evenement: React.PointerEvent<HTMLCanvasElement>): void {
    if (!dessine.current) return;
    const contexte = toile.current?.getContext('2d');
    if (!contexte) return;

    const [x, y] = position(evenement);
    contexte.lineTo(x, y);
    contexte.stroke();
    aTrace.current = true;
    if (vide) setVide(false);
  }

  function terminer(): void {
    if (!dessine.current) return;
    dessine.current = false;
    const canvas = toile.current;
    if (!canvas) return;
    // `setVide` est asynchrone : un trait qui vient d'être posé ne doit pas
    // être perdu parce que sa fermeture voit encore l'ancien état React.
    const trace = aTrace.current ? canvas.toDataURL('image/png') : null;
    derniereTraceEmise.current = trace;
    onChanger(trace);
  }

  function effacer(): void {
    const canvas = toile.current;
    const contexte = canvas?.getContext('2d');
    if (!canvas || !contexte) return;

    contexte.clearRect(0, 0, canvas.width, canvas.height);
    aTrace.current = false;
    derniereTraceEmise.current = null;
    setVide(true);
    onChanger(null);
  }

  return (
    <div className="space-y-2">
      {/* Le tracé se fait au doigt ou au stylet : il n'a pas d'équivalent au
          clavier, la signature manuscrite étant la preuve même (RGAA 7.1,
          exception du geste essentiel). La zone et son état sont annoncés. */}
      <canvas
        ref={toile}
        role="img"
        aria-label={vide ? 'Zone de signature, vide' : 'Zone de signature, tracé enregistré'}
        onPointerDown={commencer}
        onPointerMove={tracer}
        onPointerUp={terminer}
        onPointerLeave={terminer}
        className={`signature-pad ${hauteur} w-full touch-none rounded-2xl border-2 border-dashed border-gray-300 ${
          desactive ? 'cursor-not-allowed opacity-50' : 'cursor-crosshair'
        }`}
      />
      <div className="flex items-center justify-between text-[11px] text-codialis-gray">
        <span aria-live="polite">{vide ? 'Signez ici avec le doigt ou le stylet' : 'Tracé enregistré'}</span>
        <button
          type="button"
          onClick={effacer}
          disabled={desactive || vide}
          className="inline-flex items-center px-1 font-semibold text-codialis-sky hover:underline disabled:opacity-50 [@media(pointer:coarse)]:min-h-[40px] [@media(pointer:coarse)]:px-3"
        >
          Effacer
        </button>
      </div>
    </div>
  );
}
